// Asset classes: the nine keys of the top-level navigation (Stocks,
// Stablecoins, U.S. Treasuries, Non-U.S. Govt Debt, Private Credit, Money
// Market, Commodities, Private Equity, Real Estate) and the class boards behind the last seven, read from
// GET /v1/classes/{treasury|govt_debt|credit|money_market|commodity|private_equity|real_estate}/board (panther.class-board.v1)
// and GET /v1/classes (panther.class-index.v1). Types mirror the collector's
// `buildBoard` / `publishIndex` (workers/collector/src/jobs/classes.ts, P-12).
// Types, fetchers and the pure math live here so the components only render;
// nothing in the math reads the network.
//
// Withholding follows the venue boards: a missing figure is null and renders
// as "unavailable" (grid) or "—" (tiles), never 0; an entry without a value
// adds nothing to a sum. A sum over part of a class is a *known* value with its
// coverage beside it, never presented as the class total (see `classCoverage`).
//
// Development: with `npm run dev`, adding `?fixture` to the address renders the
// boards in src/fixtures/classes.fixture.ts instead of calling the API. The
// fixture import is a dynamic one behind `import.meta.env.DEV`, so production
// builds drop it (and the flag does nothing there).

import { API_BASE, chainLabel, explorerUrl, NotPublished, wantsFixture } from "./api";
import type { FlowFields } from "./flows";
import type { BoardProfile } from "./profile";

// ---------------------------------------------------------------- navigation

export type AssetClassId = "stocks" | "stablecoins" | "treasury" | "govt_debt" | "credit" | "money_market" | "commodity" | "private_equity" | "real_estate";

/** Classes with a generic class board (the API's `/v1/classes/{id}/board`). */
export type BoardClassId = Extract<AssetClassId, "treasury" | "govt_debt" | "credit" | "money_market" | "commodity" | "private_equity" | "real_estate">;

export interface AssetClass {
  id: AssetClassId;
  /** The class's id in GET /v1/classes. */
  apiId: string;
  /** Key label in the navigation. */
  label: string;
  /** Short label for phone width. */
  short: string;
  /** Path the key links to; the host serves index.html for every path. */
  path: string;
  /** Command-bar code, sector and title: `TSRY <GOVT> TREASURY TOKENS <GO>`. */
  code: string;
  sector: string;
  title: string;
  /** The line next to the navigation. */
  tag: string;
  /** What the class covers, shown under the board's meta line. */
  blurb: string;
}

/**
 * Where a class's link goes. Stocks share /tokenization with the cross-class
 * overview, which opens on All classes, so the Stocks link asks for the stocks
 * overview by name.
 */
export const classHref = (cls: Pick<AssetClass, "id" | "path">): string => (cls.id === "stocks" ? `${cls.path}?screen=dashboard` : cls.path);

export const ASSET_CLASSES: readonly AssetClass[] = [
  {
    id: "stocks",
    apiId: "stock",
    label: "Stocks",
    short: "Stocks",
    path: "/tokenization",
    code: "STKS",
    sector: "EQUITY",
    title: "STOCK TOKENS",
    tag: "Tokenized equity monitor · every chain, every issuer",
    blurb: "Tokenized stocks and ETFs across every venue and chain Panther reads.",
  },
  {
    id: "stablecoins",
    apiId: "stablecoin",
    label: "Stablecoins",
    short: "Stables",
    path: "/tokenization/stablecoins",
    code: "USD",
    sector: "CRNCY",
    title: "STABLECOINS",
    tag: "Fiat stablecoins, every currency · every chain",
    blurb: "Fiat-pegged tokens by issuer, currency and chain.",
  },
  {
    id: "treasury",
    apiId: "treasury",
    label: "U.S. Treasuries",
    short: "Treasuries",
    path: "/tokenization/treasuries",
    code: "TSRY",
    sector: "GOVT",
    title: "U.S. TREASURY TOKENS",
    tag: "Tokenized U.S. Treasury funds and bills · NAV, supply, yield",
    blurb:
      "Tokenized funds and notes backed by U.S. Treasury bills and government money-market assets. Value is on-chain supply × price per token; the issuer's reported AUM is shown beside it as a cross-check, never summed in.",
  },
  {
    id: "govt_debt",
    apiId: "govt_debt",
    label: "Non-U.S. Govt Debt",
    short: "Govt debt",
    path: "/tokenization/govt-debt",
    code: "GOVT",
    sector: "SOVEREIGN",
    title: "NON-U.S. GOVT DEBT",
    tag: "Tokenized non-U.S. government debt · by issuer and chain",
    blurb:
      "Tokenized government debt outside the United States: euro and other sovereign bills and bond funds. Prices are quoted in the fund's currency and converted to USD at the collection's FX rate.",
  },
  {
    id: "credit",
    apiId: "credit",
    label: "Private Credit",
    short: "Credit",
    path: "/tokenization/credit",
    code: "CRDT",
    sector: "CREDIT",
    title: "PRIVATE CREDIT TOKENS",
    tag: "Tokenized private credit · token market cap and protocol TVL",
    blurb:
      "Tokenized private credit vaults, pools and lending protocols. Where a protocol has no single token, its value is protocol TVL from DefiLlama and is marked as such; it is not a token market cap.",
  },
  {
    id: "money_market",
    apiId: "money_market",
    label: "Money Market",
    short: "Money mkt",
    path: "/tokenization/money-market",
    code: "MMKT",
    sector: "CASH",
    title: "MONEY MARKET TOKENS",
    tag: "Tokenized money market and cash funds · NAV, supply, value",
    blurb:
      "Tokenized money market and cash funds that are not government funds: prime money market funds and overnight swap funds. Government money market funds stay under U.S. Treasuries or Non-U.S. Govt Debt. Prices are the Chainlink NAV feed or the issuer's daily NAV, converted to USD; share classes in currencies not yet converted (CHF) are listed without a value.",
  },
  {
    id: "commodity",
    apiId: "commodity",
    label: "Commodities",
    short: "Cmdty",
    path: "/tokenization/commodities",
    code: "CMDTY",
    sector: "METALS",
    title: "COMMODITY TOKENS",
    tag: "Tokenized gold, silver and other commodities · supply, price, value",
    blurb:
      "Tokens backed by physical commodities: gold and silver by the troy ounce or gram, plus uranium. Prices come from the Chainlink gold and silver feeds, scaled for gram and fractional tokens, or CoinGecko where no ratio is set. ETF trackers that also trade on a stock venue (tokenized shares of gold, silver and oil ETFs) show their Stocks price and value and are counted under Stocks, never twice.",
  },
  {
    id: "private_equity",
    apiId: "private_equity",
    label: "Private Equity",
    short: "PE",
    path: "/tokenization/private-equity",
    code: "PRVT",
    sector: "EQUITY",
    title: "PRIVATE EQUITY TOKENS",
    tag: "Tokenized venture funds and pre-IPO exposure · what the holder owns, NAV, value",
    blurb:
      "Tokenized venture fund interests and pre-IPO products. Read what each row owns: a fund interest is equity in a fund, but pre-IPO notes, SPV interests and loan participations are not company shares. Values use a published NAV or a market price only; a product with neither is listed without a value, never with an invented mark.",
  },
  {
    id: "real_estate",
    apiId: "real_estate",
    label: "Real Estate",
    short: "Property",
    path: "/tokenization/real-estate",
    code: "PROP",
    sector: "REALTY",
    title: "REAL ESTATE TOKENS",
    tag: "Tokenized real estate · REIT shares, property vehicles and platforms",
    blurb:
      "Tokenized real estate: REIT shares, shares of the vehicle that owns one property or project, and whole platforms listed as one row at DefiLlama's TVL. Several issuers value tokens at their offering price and publish no revaluation; those rows say so.",
  },
];

export function classById(id: AssetClassId): AssetClass {
  return ASSET_CLASSES.find((c) => c.id === id)!;
}

/** Root now hosts the landing page; stocks live at /tokenization. */
export function pathToClass(pathname: string): AssetClassId | null {
  const path = pathname.replace(/\/+$/, "") || "/";
  return ASSET_CLASSES.find((c) => c.path === path)?.id ?? null;
}

export const isBoardClass = (id: AssetClassId): id is BoardClassId =>
  id === "treasury" || id === "govt_debt" || id === "credit" || id === "money_market" || id === "commodity" || id === "private_equity" || id === "real_estate";

// ---------------------------------------------------------------- GET /v1/classes/{class}/board

export type ValueBasis = "market_cap" | "tvl";

export interface ClassDeployment {
  chain: string;
  address: string;
  supply: number | null;
}

export interface ClassEntry extends FlowFields {
  /** Registry slug, unique within the class. */
  id: string;
  /**
   * DQ-12b: "distributed", a token investors hold and transfer; "represented", an on-chain record of
   * off-chain assets (Figure HELOC), listed and valued but left out of every distributed figure.
   * Absent on older boards (distributed).
   */
  representation?: Representation;
  symbol: string;
  name: string;
  issuer: { id: string; name: string; url: string | null };
  /** C-6d: verified holders summed across the chains counted (an address on two chains counts twice); null until counted. */
  holders?: number | null;
  /** Share (0–1) of the entry's value on chains with a verified count. */
  holders_coverage?: number | null;
  /** Why holders is null or partial, for humans. */
  holders_note?: string | null;
  /** Addresses that sent or received the token in the last 30 days, summed across chains. */
  active_addresses_30d?: number | null;
  active_addresses_coverage?: number | null;
  active_addresses_note?: string | null;
  /** Id of the protocol-level entry whose TVL already contains this one; left out of totals. */
  included_in?: string | null;
  /** DQ-15: cited fund facts (identifiers, fees, minimums, service providers); absent on older boards. */
  profile?: Partial<BoardProfile> | null;
  /** The platform it is issued through (C-7b); absent on older boards. */
  platform?: { id: string; name: string; url: string | null } | null;
  /** Free-form grouping: money_market_fund, treasury_note, lending_pool, ... */
  sub_class: string;
  /** ISO 4217 code the product is denominated in. */
  currency: string;
  /** `<chain>:<address>` of the primary deployment, or `offchain:<id>`. */
  token_id: string;
  chain: string;
  chains: string[];
  deployments: ClassDeployment[];
  /** Price per token in USD (after FX). */
  price: number | null;
  /** The registry's price type: fixed, nav_api, coingecko, chainlink, onchain_oracle, erc4626, unknown. */
  price_basis: string | null;
  price_provider: string | null;
  price_at: string | null;
  /** Price in `price_currency`, before FX. */
  native_price: number | null;
  price_currency: string;
  /** USD per unit of `price_currency`; null when no conversion was needed or it failed. */
  fx_rate: number | null;
  supply: number | null;
  /** Whether every deployment's supply was read; null without deployments. */
  supply_complete: boolean | null;
  value_usd: number | null;
  value_basis: ValueBasis;
  /** Fraction, 0.0452 = 4.52%. */
  yield_apy: number | null;
  /** The issuer's own AUM figure, USD; a cross-check on value_usd, never summed into it. */
  aum_usd_reported: number | null;
  change_24h: { price: number | null; value_usd: number | null };
  /** C-6b: how the yield is found. nav_accrual: from Panther's NAV history; issuer: the issuer's published yield; none. */
  yield_method?: YieldMethod | null;
  /** Annualized NAV yields over 7 and 30 days, fractions; null until enough NAV history (see yield_note). */
  yield_7d?: number | null;
  yield_30d?: number | null;
  /** The issuer's published yield, fraction. */
  yield_published?: number | null;
  /** What yield_published measures, e.g. "7-day SEC yield (net), month-end, SEC Form N-MFP3"; null when the source does not say. */
  yield_basis?: string | null;
  /** Which figure is the entry's yield, the one the class average uses. */
  yield_source?: YieldSource | null;
  yield_as_of?: string | null;
  /** Why the yield, or one of the NAV yields, is null. */
  yield_note?: string | null;
  /** 7-day market change as a fraction (0.05 = +5%), like for like over the same products and chains; null until a week of snapshots exists, never 0 for unknown. */
  change_7d?: number | null;
  change_7d_usd?: number | null;
  /** 30-day change, as change_7d. */
  change_30d?: number | null;
  change_30d_usd?: number | null;
  /** C-6a-fix: value from products newly tracked minus value of those dropped (change_*_usd is like for like). */
  coverage_change_7d_usd?: number | null;
  coverage_change_30d_usd?: number | null;
  /** Market plus coverage: the raw change in value. */
  total_change_7d_usd?: number | null;
  total_change_30d_usd?: number | null;
  links: Record<string, string> | string[] | null;
  /** Every deployment confirmed against the issuer's material; null without deployments. */
  verified: boolean | null;
  notes: string | null;
  /** One or two plain sentences for readers: what it is, what the holder owns, the issuer. Absent on older boards. */
  summary?: string | null;
  /** What kind of instrument the token is (lending_pool, clo_fund, private_credit_note, ...); a secondary label. */
  instrument_type?: string | null;
  /** ETF trackers: the Stocks token this entry mirrors; its price and value are shown, never summed here. */
  stock_token_id?: string | null;
  /** Why figures are missing this slot: "ethereum supply: timeout", "price: …". */
  problems: string[];
}

export interface ClassIssuerTotal {
  id: string;
  name: string;
  value_usd: number | null;
  entries: number;
}

export interface ClassChainTotal {
  chain: string;
  /** Supply-valued entries only: a protocol's TVL cannot be attributed to a chain. */
  value_usd: number | null;
  entries: number;
}

export interface ClassBoard {
  schema: "panther.class-board.v1";
  asset_class: BoardClassId;
  name: string;
  slot: number;
  as_of: string;
  generated_at: string;
  totals: FlowFields & {
  /** DQ-12b: represented entries (on-chain records of off-chain assets investors cannot hold), counted apart from every figure above. */
  represented_entries?: number | null;
  /** Their value summed, apart from value_usd; null when any has no value this run. */
  represented_value_usd?: number | null;
  /** value_usd + represented_value_usd; null when either is null. */
  total_incl_represented_usd?: number | null;
    entries: number;
    /** Entries with a price. */
    priced: number;
    /** Entries with a supply. */
    supplied: number;
    /** Entries with a value (market cap or TVL). */
    valued: number;
    /** Sum of the known values: covers `valued` of `entries`. */
    value_usd: number | null;
    by_basis: { market_cap: number | null; tvl: number | null };
    /** C-6b: value-weighted average yield of the entries with one (treasury, money_market; null elsewhere or under 50% coverage). */
    avg_yield?: number | null;
    /** Share (0–1) of the counted value held by entries with a yield. */
    avg_yield_coverage?: number | null;
    avg_yield_entries?: number | null;
    /** SOFR the spread is taken against, fraction (0.039 = 3.90%), and its effective date. */
    sofr?: number | null;
    /** C-6d: the sum of per-product holders, under the 90% rule; null until then. */
    holders?: number | null;
    holders_coverage?: number | null;
    holders_entries?: number | null;
    active_addresses_30d?: number | null;
    active_addresses_coverage?: number | null;
    active_addresses_entries?: number | null;
    /** How the class's holders and active addresses are counted. */
    holders_note?: string | null;
    /** That SOFR as an annual compounded yield, (1 + sofr/360)^365 − 1: the basis the spread is taken against. */
    sofr_apy?: number | null;
    sofr_date?: string | null;
    /** avg_yield − sofr, fraction. */
    avg_yield_spread?: number | null;
    /** Why avg_yield or the spread is null. */
    avg_yield_note?: string | null;
    /** 7-day market change as a fraction (0.05 = +5%), like for like over the same products and chains; null until a week of snapshots exists, never 0 for unknown. */
    change_7d?: number | null;
    change_7d_usd?: number | null;
    /** 30-day change, as change_7d. */
    change_30d?: number | null;
    change_30d_usd?: number | null;
    /** C-6a-fix: value from products newly tracked minus value of those dropped (change_*_usd is like for like). */
    coverage_change_7d_usd?: number | null;
    coverage_change_30d_usd?: number | null;
    /** Market plus coverage: the raw change in value. */
    total_change_7d_usd?: number | null;
    total_change_30d_usd?: number | null;
  };
  issuers: ClassIssuerTotal[];
  /** The platforms the entries are issued through (Securitize, Libre…), totalled like issuers (C-7b). */
  platforms?: ClassIssuerTotal[];
  chains: ClassChainTotal[];
  entries: ClassEntry[];
}

// ---------------------------------------------------------------- GET /v1/classes

export interface ClassIndexItem extends FlowFields {
  /** C-6d: the class's holders and 30-day active addresses, under the 90% rule. */
  holders?: number | null;
  holders_coverage?: number | null;
  active_addresses_30d?: number | null;
  active_addresses_coverage?: number | null;
  /** treasury | govt_debt | credit | money_market | stock | stablecoin. */
  id: string;
  name: string;
  available: boolean;
  endpoint: string | null;
  as_of: string | null;
  totals: FlowFields & { entries: number | null; priced: number | null; value_usd: number | null; valued?: number | null; change_7d?: number | null; change_30d?: number | null };
  /** DQ-12b: represented entries (on-chain records of off-chain assets investors cannot hold), counted apart from every figure above. */
  represented_entries?: number | null;
  /** Their value summed, apart from value_usd; null when any has no value this run. */
  represented_value_usd?: number | null;
  /** value_usd + represented_value_usd; null when either is null. */
  total_incl_represented_usd?: number | null;

  /** 7-day market change as a fraction (0.05 = +5%), like for like over the same products and chains; null until a week of snapshots exists, never 0 for unknown. */
  change_7d?: number | null;
  change_7d_usd?: number | null;
  /** 30-day change, as change_7d. */
  change_30d?: number | null;
  change_30d_usd?: number | null;
  /** C-6a-fix: value from products newly tracked minus value of those dropped (change_*_usd is like for like). */
  coverage_change_7d_usd?: number | null;
  coverage_change_30d_usd?: number | null;
  /** Market plus coverage: the raw change in value. */
  total_change_7d_usd?: number | null;
  total_change_30d_usd?: number | null;
}

export type YieldMethod = "nav_accrual" | "issuer" | "none";
export type YieldSource = "nav_30d" | "nav_7d" | "issuer";

const YIELD_SOURCE_LABELS: Record<YieldSource, string> = {
  nav_30d: "30-day yield from the fund's NAV history",
  nav_7d: "7-day yield from the fund's NAV history",
  issuer: "Yield published by the issuer",
};

export interface EntryYield {
  /** Fraction; null when the entry has no yield. */
  value: number | null;
  source: YieldSource | null;
  /** Hover text: where the figure comes from and its date, or why there is none. */
  title: string | null;
}

/**
 * The entry's yield as the class average uses it (`yield_source`): the 30-day
 * NAV yield, else the 7-day one, else the issuer's. Boards from before C-6b
 * fall back to the registry's static `yield_apy`.
 */
export function entryYield(
  e: Pick<ClassEntry, "yield_apy" | "yield_source" | "yield_7d" | "yield_30d" | "yield_published" | "yield_as_of" | "yield_note"> & { yield_basis?: string | null },
): EntryYield {
  const pick = (source: YieldSource): number | null | undefined => (source === "nav_30d" ? e.yield_30d : source === "nav_7d" ? e.yield_7d : e.yield_published);
  if (e.yield_source) {
    const value = pick(e.yield_source);
    if (value !== null && value !== undefined && Number.isFinite(value)) {
      // An issuer figure says what it measures (a 7-day SEC yield is simple, not compounded).
      const basis = e.yield_source === "issuer" && e.yield_basis ? `: ${e.yield_basis}` : "";
      return { value, source: e.yield_source, title: `${YIELD_SOURCE_LABELS[e.yield_source]}${basis}${e.yield_as_of ? `, as of ${e.yield_as_of}` : ""}` };
    }
  }
  if (e.yield_source === undefined && e.yield_apy !== null && Number.isFinite(e.yield_apy)) {
    return { value: e.yield_apy, source: "issuer", title: "Issuer-reported yield (registry)" };
  }
  return { value: null, source: null, title: e.yield_note ?? null };
}

/** "+0.25 pt" for a spread between two rates (fractions). */
export function spreadText(spread: number): string {
  const pt = spread * 100;
  return `${pt > 0 ? "+" : pt < 0 ? "−" : ""}${Math.abs(pt).toFixed(2)} pt`;
}

export interface ClassIndex extends FlowFields {
  /** C-6d: the sum of per-product holders over the registry classes and stock (stablecoins excluded); null under 90% coverage. */
  total_asset_holders?: number | null;
  total_asset_holders_coverage?: number | null;
  /** How total_asset_holders is counted, including why stablecoins are left out. */
  total_asset_holders_note?: string | null;
  schema: "panther.class-index.v1";
  slot: number;
  as_of: string;
  generated_at: string;
  /** The headline: every class summed, each product counted once (net of `overlap`). */
  total_value_usd: number | null;
  /** DQ-12b: represented entries (on-chain records of off-chain assets investors cannot hold), counted apart from every figure above. */
  represented_entries?: number | null;
  /** Their value summed, apart from value_usd; null when any has no value this run. */
  represented_value_usd?: number | null;
  /** total_value_usd + represented_value_usd; null when either is null. */
  total_incl_represented_usd?: number | null;

  /** 7-day change of total_value_usd as a fraction (0.05 = +5%); null until a week of daily snapshots exists, never 0 for unknown. */
  change_7d?: number | null;
  change_7d_usd?: number | null;
  /** 30-day change, as change_7d. */
  change_30d?: number | null;
  change_30d_usd?: number | null;
  /** C-6a-fix: value from products newly tracked minus value of those dropped (change_*_usd is like for like). */
  coverage_change_7d_usd?: number | null;
  coverage_change_30d_usd?: number | null;
  /** Market plus coverage: the raw change in value. */
  total_change_7d_usd?: number | null;
  total_change_30d_usd?: number | null;
  /** The plain sum of the classes, before the overlap is taken out. Absent before U-20's API change. */
  gross_value_usd?: number | null;
  overlap_value_usd?: number | null;
  /** Products counted in a registry class and in the stablecoin list, valued once in the headline. */
  overlap?: ClassOverlap[];
  /** Stablecoins that look like a registry product but could not be matched by contract. */
  overlap_unchecked?: unknown[];
  /**
   * Contracts a registry class values that are also a stock venue token, so both count them. Should be
   * empty; not deducted. Each is also in overlap_unchecked.
   */
  venue_overlap?: VenueOverlap[];
  note?: string | null;
  classes: ClassIndexItem[];
}

export interface VenueOverlap {
  class: string;
  entry: string;
  entry_symbol: string;
  token_id: string;
  /** The stock venue whose token carries the same contract. */
  venue: string;
  venue_token_id: string;
  value_usd: number | null;
  source: "db" | "board";
}

export interface ClassOverlap {
  /** The registry class it is counted in (treasury, ...). */
  class: string;
  entry: string;
  entry_symbol: string;
  stablecoin: { id: string; symbol: string; name: string };
  value_usd: number | null;
  /** Left out of the headline total. */
  deducted: boolean;
  /**
   * `same_token`: the fund is itself listed as a stablecoin. `backed_by`: the
   * stablecoin holds the fund in its reserve, so `value_usd` is the fund
   * amount held. Absent before the API's backed_by change: read as same_token.
   */
  reason?: OverlapReason;
  /** For `backed_by`: what the reserve holds and where it was read. */
  backing?: OverlapBacking | null;
  /** Where a reserve holding sits, by chain (C-7b): what Networks deducts per chain. */
  by_chain?: Array<{ chain: string; amount_usd: number | null }>;
}

export type OverlapReason = "same_token" | "backed_by";

export interface OverlapBacking {
  /** What the reserve holds: a fund a class counts, or another stablecoin (USDe holds USDtb). Absent means a fund. */
  target?: "fund" | "stablecoin";
  method?: string;
  estimate?: boolean;
  fund_units?: number | null;
  fund_price_usd?: number | null;
  fund_amount_usd: number | null;
  stablecoin_value_usd?: number | null;
  holders?: Array<{ chain: string; address: string; label?: string | null; units?: number | null }>;
  source_url?: string | null;
  note?: string | null;
}

const BOARD_SCHEMA = "panther.class-board.v1";
const INDEX_SCHEMA = "panther.class-index.v1";

export async function fetchClassBoard(classId: BoardClassId, signal?: AbortSignal): Promise<ClassBoard> {
  if (import.meta.env.DEV && wantsFixture()) return (await import("./fixtures/classes.fixture")).CLASS_FIXTURES[classId];
  const response = await fetch(`${API_BASE}/v1/classes/${classId}/board`, { signal });
  if (response.status === 404) throw new NotPublished();
  if (!response.ok) throw new Error(`The API answered ${response.status}`);
  const board = (await response.json()) as ClassBoard;
  if (board?.schema !== BOARD_SCHEMA || !Array.isArray(board.entries)) throw new Error("Unexpected board format");
  return board;
}

export async function fetchClassIndex(signal?: AbortSignal): Promise<ClassIndex> {
  if (import.meta.env.DEV && wantsFixture()) return (await import("./fixtures/classes.fixture")).CLASS_INDEX_FIXTURE;
  const response = await fetch(`${API_BASE}/v1/classes`, { signal });
  if (response.status === 404) throw new NotPublished();
  if (!response.ok) throw new Error(`The API answered ${response.status}`);
  const body = (await response.json()) as ClassIndex;
  if (body?.schema !== INDEX_SCHEMA || !Array.isArray(body.classes)) throw new Error("Unexpected classes format");
  return body;
}

// ---------------------------------------------------------------- representation (DQ-12b)

export type Representation = "distributed" | "represented";

/** An on-chain record of off-chain assets investors cannot hold or transfer (rwa.xyz's "represented"). */
export const isRepresented = (entry: Pick<ClassEntry, "representation">): boolean => entry.representation === "represented";

/** The entries every distributed figure counts: all but the represented ones. */
export const distributedEntries = <T extends Pick<ClassEntry, "representation">>(entries: readonly T[]): T[] => entries.filter((e) => !isRepresented(e));

export const REPRESENTED_MEANING =
  "Represented: an on-chain record of off-chain assets that investors cannot hold or transfer. Listed and valued, but left out of every total, count and flow figure.";

export interface RepresentedLine {
  entries: number;
  /** Their value summed; null when any has no value this run (a partial sum is never shown). */
  valueUsd: number | null;
  /** The distributed total plus the represented value; null when either is unknown. */
  inclusiveUsd: number | null;
}

/** The "Represented: $23.7B" line for a board, index row or headline; null when nothing is represented. */
export function representedLine(totals: { represented_entries?: number | null; represented_value_usd?: number | null; total_incl_represented_usd?: number | null }): RepresentedLine | null {
  const entries = totals.represented_entries ?? 0;
  if (!entries) return null;
  return { entries, valueUsd: totals.represented_value_usd ?? null, inclusiveUsd: totals.total_incl_represented_usd ?? null };
}

// ---------------------------------------------------------------- coverage

export interface Coverage {
  entries: number;
  valued: number;
  /** Valued entries whose supply was read on only some of their chains (their value is a floor). */
  partialSupply: number;
  /** ETF trackers listed on the board but valued under Stocks; not counted in `entries`. */
  trackers: number;
  /** valued / entries; null for an empty class. */
  share: number | null;
  /** Every entry valued in full: the sum may be shown as the class total. */
  complete: boolean;
}

/**
 * The coverage rule for class totals. A sum is the class total only when every
 * entry has a value and none of those values is a partial-supply floor;
 * otherwise it is a known value covering `valued` of `entries`.
 */
export function classCoverage(board: Pick<ClassBoard, "totals" | "entries">): Coverage {
  // ETF trackers the API leaves unvalued are valued under Stocks: listed here, never expected to carry a value.
  // A tracker the API does value is in the class total like any entry (totals.valued counts it).
  // Represented entries (DQ-12b) are outside every distributed count, as they are outside totals.entries.
  const distributed = distributedEntries(board.entries);
  const trackers = distributed.filter((e) => isEtfTracker(e) && e.value_usd === null).length;
  const entries = board.totals.entries - trackers;
  const valued = board.totals.valued ?? distributed.filter((e) => e.value_usd !== null).length;
  const partialSupply = distributed.filter(
    (e) => e.value_usd !== null && e.value_basis === "market_cap" && e.supply_complete === false,
  ).length;
  return {
    entries,
    valued,
    trackers,
    partialSupply,
    share: entries > 0 ? valued / entries : null,
    complete: entries > 0 && valued === entries && partialSupply === 0,
  };
}

/**
 * Coverage of one class in the index. Only `valued` (entries with a value) can
 * make a sum complete; the index today sends `priced` only, and a priced entry
 * can still lack a supply, so a priced count reports coverage but never makes
 * the sum a total. Null when the index reports neither.
 */
export function indexCoverage(item: ClassIndexItem): { known: number; of: number; basis: "valued" | "priced"; complete: boolean } | null {
  const of = item.totals.entries;
  if (of === null) return null;
  const valued = item.totals.valued;
  if (typeof valued === "number") return { known: valued, of, basis: "valued", complete: of > 0 && valued >= of };
  const priced = item.totals.priced;
  if (priced === null) return null;
  return { known: priced, of, basis: "priced", complete: false };
}

/**
 * Why a class value is a floor, for the value tile's note: "40 of 46 valued,
 * not a class total" when entries lack a value; "46 of 46 valued · 1 with
 * supply read on only some chains" when every entry is valued but some
 * supplies are partial. Null for a complete total.
 */
export function partialValueNote(coverage: Pick<Coverage, "entries" | "valued" | "partialSupply" | "complete">, fmt: (n: number) => string = String): string | null {
  if (coverage.complete) return null;
  const valued = `${fmt(coverage.valued)} of ${fmt(coverage.entries)} valued`;
  const partial = coverage.partialSupply ? `${fmt(coverage.partialSupply)} with supply read on only some chains` : null;
  if (coverage.valued < coverage.entries) return [`${valued}, not a class total`, partial].filter(Boolean).join(" · ");
  return [valued, partial].filter(Boolean).join(" · ");
}

/**
 * A registry note split for display: its first sentence, and the rest (often a
 * long research trail) to show on request. A sentence ends at ". " before a
 * capital, so "U.S. cities" and "e.g. a fund" stay whole.
 */
export function noteParts(note: string | null | undefined): { lead: string; rest: string | null } | null {
  return splitNote(note);
}

/**
 * What a reader sees for an entry: the registry's plain summary, with the full
 * research note on request; without a summary, the note's first sentence and
 * the rest on request.
 */
export function entryNote(entry: { summary?: string | null; notes: string | null }): { lead: string; rest: string | null } | null {
  const summary = entry.summary?.trim();
  if (summary) return { lead: summary, rest: entry.notes?.trim() || null };
  return splitNote(entry.notes);
}

function splitNote(note: string | null | undefined): { lead: string; rest: string | null } | null {
  const text = note?.trim();
  if (!text) return null;
  const end = text.search(/[.!?] (?=[A-Z(])/);
  if (end === -1) return { lead: text, rest: null };
  return { lead: text.slice(0, end + 1), rest: text.slice(end + 2).trim() || null };
}

/** "$4.21B" for a complete total, "≥ $4.21B" for a partial one, "—" when nothing is known. */
export function totalText(value: number | null, complete: boolean, format: (v: number) => string): string {
  if (value === null) return "—";
  return complete ? format(value) : `≥ ${format(value)}`;
}

// ---------------------------------------------------------------- labels

/** Grid badge for a price basis (the registry's price type). */
const PRICE_BASIS_LABELS: Record<string, string> = {
  fixed: "fixed",
  nav_api: "NAV",
  coingecko: "CoinGecko",
  chainlink: "Chainlink",
  onchain_oracle: "oracle",
  erc4626: "ERC-4626",
};

const PRICE_BASIS_MEANINGS: Record<string, string> = {
  fixed: "Fixed at par: the token is redeemable 1:1",
  nav_api: "Net asset value per token, from the issuer's NAV feed",
  coingecko: "Market price, as CoinGecko reports it",
  chainlink: "Chainlink price feed",
  onchain_oracle: "On-chain oracle price from the issuer's contract",
  erc4626: "Vault share price from the contract (ERC-4626 convertToAssets)",
};

/** Badge text for a price basis; null when there is none to show. */
export function priceBasisLabel(basis: string | null | undefined): string | null {
  if (!basis || basis === "unknown") return null;
  return PRICE_BASIS_LABELS[basis] ?? basis.replaceAll("_", " ");
}

export function priceBasisMeaning(basis: string | null | undefined): string | undefined {
  return basis ? PRICE_BASIS_MEANINGS[basis] : undefined;
}

export const valueBasisLabel = (basis: ValueBasis): string => (basis === "tvl" ? "protocol TVL" : "market cap");

const SUB_CLASS_LABELS: Record<string, string> = {
  us_treasury_fund: "U.S. Treasury fund",
  us_treasury_bill: "U.S. T-bill",
  treasury_note: "Treasury note",
  treasury_backed_note: "Treasury-backed note",
  money_market_fund: "Money-market fund",
  eur_treasury_bill_fund: "EUR T-bill fund",
  non_us_govt_bond_etf: "Govt bond ETF",
  non_us_govt_bond: "Govt bond",
  sovereign_bill_fund: "Sovereign bill fund",
  private_credit_vault: "Private credit vault",
  private_credit_protocol: "Private credit protocol",
  lending_pool: "Lending pool",
  corporate_bond_etf: "Corporate bond ETF",
  consumer_credit: "Consumer credit",
  protocol: "Protocol",
  prime_money_market_fund: "Prime money-market fund",
  overnight_swap_fund: "Overnight swap fund",
  gold: "Gold",
  silver: "Silver",
  gold_in_situ: "Gold (in situ)",
  uranium: "Uranium",
  etf_tracker: "ETF tracker",
  tracker_certificate: "Tracker certificate",
  venture_fund: "Venture fund",
  pre_ipo_note: "Pre-IPO note",
  pre_ipo_spv: "Pre-IPO SPV",
  pre_ipo_loan_participation: "Pre-IPO loan participation",
  reit: "REIT",
  real_estate_fund: "Real estate fund",
  property_platform: "Property platform",
  property_token: "Property token",
  asset_backed: "Asset-backed",
  corporate: "Corporate",
  specialty_finance: "Specialty finance",
  diversified: "Diversified",
  municipal: "Municipal",
  asset_backed_credit: "Asset-backed credit",
  credit_protocol: "Credit protocol",
  private_credit_note: "Private credit note",
  private_credit_fund: "Private credit fund",
  consumer_credit_vault: "Consumer credit vault",
  trade_finance: "Trade finance",
  clo_fund: "CLO fund",
};

/** Credit's groups, in the order the filter shows them (rwa.xyz's grouping). */
export const CREDIT_GROUPS = ["asset_backed", "corporate", "specialty_finance", "diversified", "municipal"] as const;

export interface GroupTotal {
  id: string;
  label: string;
  entries: number;
  /** Sum of the group's known values; null when none is valued. */
  valueUsd: number | null;
  /** Every entry in the group carries a value, so the sum is the group's total. */
  complete: boolean;
}

/**
 * Totals per group, from the board's own entries, in `order`; groups with no entries
 * are left out (credit's municipal group is empty today). Null when an entry's
 * sub-class is outside `order`, so a board not yet regrouped shows no group filter.
 */
export function groupTotals(entries: ReadonlyArray<Pick<ClassEntry, "sub_class" | "value_usd">>, order: readonly string[]): GroupTotal[] | null {
  if (!entries.length || entries.some((e) => !order.includes(e.sub_class))) return null;
  return order.flatMap((id) => {
    const members = entries.filter((e) => e.sub_class === id);
    if (!members.length) return [];
    const valued = members.filter((e) => e.value_usd !== null && Number.isFinite(e.value_usd));
    return [
      {
        id,
        label: subClassLabel(id) ?? id,
        entries: members.length,
        valueUsd: valued.length ? valued.reduce((n, e) => n + (e.value_usd as number), 0) : null,
        complete: valued.length === members.length,
      },
    ];
  });
}

/** What a private-equity row's holder actually owns. Anything but a fund interest is not company shares. */
const OWNERSHIP: Record<string, { label: string; shares: boolean; meaning: string }> = {
  venture_fund: { label: "Fund interest", shares: true, meaning: "An equity interest in a venture fund, which holds the companies." },
  pre_ipo_note: {
    label: "Pre-IPO note — not shares",
    shares: false,
    meaning: "A note from the issuer that pays out on the company's IPO or sale: unsecured debt of the issuer, not shares of the company.",
  },
  pre_ipo_spv: {
    label: "SPV interest — not shares",
    shares: false,
    meaning: "A non-voting interest in a vehicle that says it holds the company's shares: not shares of the company, and the company may dispute the transfer.",
  },
  pre_ipo_loan_participation: {
    label: "Loan participation — not shares",
    shares: false,
    meaning: "A participation in a loan with economic exposure to the company: not shares, and not a security by the issuer's account.",
  },
};

/** The ownership badge for a private-equity row, from its sub-class; null for other classes. */
/** Real estate: what a row's holder owns, from its sub-class and, for project tokens, its issuer. */
const REAL_ESTATE: Record<string, { label: string; shares: boolean; meaning: string }> = {
  reit: { label: "REIT shares", shares: true, meaning: "Shares of a real estate investment trust that owns a portfolio of buildings." },
  real_estate_fund: { label: "Fund interest", shares: true, meaning: "An interest in a fund that owns and runs real estate." },
  property_platform: {
    label: "Platform total (DefiLlama)",
    shares: true,
    meaning: "One row for a whole platform's property tokens (each an interest in the vehicle owning one property), valued at DefiLlama's TVL for the platform.",
  },
  property_token: { label: "Property-SPV equity", shares: true, meaning: "A share of the vehicle that owns one property or project, with rental income and sale proceeds." },
};

/** Reental tokens carry property equity or a development loan, project by project; the registry has not checked which. */
const REENTAL = { label: "Project vehicle — equity or loan", shares: true, meaning: "A share of one Reental project vehicle: property equity with rental income, or a development loan, depending on the project (not checked token by token)." };

export const ownership = (
  entry: Pick<ClassEntry, "sub_class"> & Partial<Pick<ClassEntry, "issuer">>,
): { label: string; shares: boolean; meaning: string } | null => {
  if (!entry.sub_class) return null;
  if (entry.sub_class === "property_token" && entry.issuer?.id === "reental") return REENTAL;
  return OWNERSHIP[entry.sub_class] ?? REAL_ESTATE[entry.sub_class] ?? null;
};

/** A private-equity or real-estate row with no price or value: its manager publishes no NAV Panther can read. */
export const noPublishedNav = (entry: Pick<ClassEntry, "sub_class" | "price"> & Partial<Pick<ClassEntry, "value_basis" | "issuer">>): boolean =>
  ownership(entry) !== null && entry.price === null && entry.value_basis !== "tvl";

/**
 * A value that is an offering or primary-sale price the issuer never revalues (Reental,
 * MetaWealth): said on the row, from the registry note's own wording.
 */
export function offeringPriceNote(entry: Pick<ClassEntry, "notes" | "price" | "price_basis">): string | null {
  const notes = entry.notes ?? "";
  if (entry.price === null || entry.price_basis !== "fixed") return null;
  if (!/(primary-sale|offering) price/i.test(notes) || !/no revaluation/i.test(notes)) return null;
  return `Offering price ($${entry.price}), no revaluation published: gains, losses and returned capital are not reflected`;
}

/**
 * Short caveats shown on a row, from the registry's research notes. BCAP's NAV is the
 * manager's mark, which stepped from about $23 (through March 2026) to $83 and $106 in
 * April 2026 with no change in supply (packages/registry/data/private-equity.json).
 */
export const ENTRY_CAVEATS: Record<string, string> = {
  "blockchain-capital-bcap":
    "NAV rose ~4.6× in Apr 2026 (≈$23 → $106), a manager mark with no published explanation; official oracle (RedStone), matches DefiLlama and rwa.xyz",
};

/**
 * An ETF tracker that also trades on a stock venue: listed here for reference,
 * counted under Stocks only. The API marks it with stock_token_id. A tracker
 * certificate sold only by its issuer (Backed's bIB01, bC3M…) has none and is
 * valued in its class like any entry.
 */
export const isEtfTracker = (entry: Pick<ClassEntry, "stock_token_id">): boolean => Boolean(entry.stock_token_id);

/** The Stocks token an ETF tracker mirrors; null for anything counted in its class. */
export const trackerStockId = (entry: Pick<ClassEntry, "stock_token_id">): string | null => entry.stock_token_id || null;

const UNITS: Record<string, string> = { "troy ounce": "troy oz", gram: "g", pound: "lb" };

/**
 * The commodity one token stands for, from the registry note's own wording
 * ("1 XAUt = 1 fine troy ounce…", "each token is 1/1000 troy ounce…"):
 * "1 token = 1 troy oz". Null when the note states no ratio.
 */
export function unitLabel(entry: Pick<ClassEntry, "notes">): string | null {
  const notes = entry.notes ?? "";
  const match =
    /\b1 [^\s=]+ = (\d+(?:\/\d+)?) (?:fine )?(troy ounce|gram|pound)s?\b/i.exec(notes) ??
    /\beach token is (\d+(?:\/\d+)?) (?:fine )?(troy ounce|gram|pound)s?\b/i.exec(notes);
  if (!match) return null;
  return `1 token = ${match[1]} ${UNITS[match[2]!.toLowerCase()]}`;
}

export function subClassLabel(subClass: string | null | undefined): string | null {
  if (!subClass) return null;
  const known = SUB_CLASS_LABELS[subClass];
  if (known) return known;
  const words = subClass.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Chain display names live in api.ts: one map for venue and class boards. */
export { chainLabel };

/** A price in a currency: "$1.0523", "€98.73"; prices under 10 keep four decimals. */
export function formatPrice(value: number | null | undefined, currency: string): string {
  if (value === null || value === undefined) return "unavailable";
  const digits = Math.abs(value) < 10 ? 4 : 2;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: digits,
    }).format(value);
  } catch {
    return `${value.toFixed(digits)} ${currency}`;
  }
}

/**
 * The entry's links as label/url pairs: the registry's named links (or a bare
 * list, labelled by host), then an explorer link for the primary deployment
 * when the registry gives none. Only http(s) URLs pass.
 */
export function entryLinks(entry: Pick<ClassEntry, "links" | "deployments" | "token_id">): Array<{ label: string; url: string }> {
  const out: Array<{ label: string; url: string }> = [];
  const add = (label: string, url: unknown) => {
    if (typeof url !== "string" || !/^https?:\/\//i.test(url) || out.some((l) => l.url === url)) return;
    out.push({ label, url });
  };
  const host = (url: string) => {
    try {
      return new URL(url).hostname.replace(/^www\./, "");
    } catch {
      return "link";
    }
  };
  if (Array.isArray(entry.links)) for (const url of entry.links) add(typeof url === "string" ? host(url) : "link", url);
  else if (entry.links && typeof entry.links === "object") for (const [label, url] of Object.entries(entry.links)) add(label.replaceAll("_", " "), url);
  if (!out.some((l) => l.label === "explorer")) {
    const primary = entry.deployments.find((d) => `${d.chain}:${d.address}`.toLowerCase() === entry.token_id.toLowerCase()) ?? entry.deployments[0];
    const url = primary ? explorerUrl(primary.chain, primary.address) : null;
    if (url) add("explorer", url);
  }
  return out;
}

/** The token panel (U-3) reads /v1/tokens/{id}; only supply-valued entries with an on-chain id have a meaningful one. */
export const hasTokenPanel = (entry: Pick<ClassEntry, "token_id" | "value_basis">): boolean =>
  entry.value_basis === "market_cap" && !entry.token_id.startsWith("offchain:");

// ---------------------------------------------------------------- league math

/** Sum of the finite values; null when there are none. */
export const sumOrNull = (values: ReadonlyArray<number | null | undefined>): number | null => {
  const known = values.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  return known.length ? known.reduce((total, v) => total + v, 0) : null;
};

export interface LeaguePart {
  key: string;
  label: string;
  valueUsd: number | null;
  entries: number;
}

export interface LeagueRow extends LeaguePart {
  /** Share of the summed value; null for a row without a value. */
  share: number | null;
}

/**
 * Ranks parts by value with each one's share of their sum. Rows without a
 * value get a null share and sort last (most entries first); a real zero is a
 * value and gets share 0.
 */
export function league(parts: readonly LeaguePart[]): LeagueRow[] {
  const total = parts.reduce((sum, part) => sum + (part.valueUsd ?? 0), 0);
  return parts
    .map((part) => ({ ...part, share: part.valueUsd === null ? null : total > 0 ? part.valueUsd / total : 0 }))
    .sort((a, b) => {
      if (a.valueUsd === null || b.valueUsd === null) {
        return (a.valueUsd === null ? 1 : 0) - (b.valueUsd === null ? 1 : 0) || b.entries - a.entries;
      }
      return b.valueUsd - a.valueUsd || b.entries - a.entries;
    });
}

/** Issuer totals from the entries, for a board that does not carry them (same rule as the collector). */
export function deriveIssuers(entries: readonly ClassEntry[]): ClassIssuerTotal[] {
  const byIssuer = new Map<string, { name: string; values: Array<number | null> }>();
  for (const entry of distributedEntries(entries)) {
    const item = byIssuer.get(entry.issuer.id) ?? { name: entry.issuer.name, values: [] };
    item.values.push(entry.value_usd);
    byIssuer.set(entry.issuer.id, item);
  }
  return [...byIssuer].map(([id, item]) => ({ id, name: item.name, value_usd: sumOrNull(item.values), entries: item.values.length }));
}

/**
 * Chain totals from the entries, for a board that does not carry them, by the
 * collector's rule: a deployment contributes supply × price only for a
 * supply-valued entry with a price and a read supply. Every deployment counts
 * its entry on that chain; a chain with nothing attributable has value null.
 */
export function deriveChains(entries: readonly ClassEntry[]): ClassChainTotal[] {
  const byChain = new Map<string, { values: number[]; entries: Set<string> }>();
  for (const entry of distributedEntries(entries)) {
    for (const d of entry.deployments) {
      const item = byChain.get(d.chain) ?? { values: [], entries: new Set<string>() };
      item.entries.add(entry.id);
      if (entry.value_basis === "market_cap" && d.supply !== null && entry.price !== null) item.values.push(d.supply * entry.price);
      byChain.set(d.chain, item);
    }
  }
  return [...byChain].map(([chain, item]) => ({ chain, value_usd: item.values.length ? sumOrNull(item.values) : null, entries: item.entries.size }));
}

export function issuerLeague(board: Pick<ClassBoard, "issuers" | "entries">): LeagueRow[] {
  const totals = board.issuers.length ? board.issuers : deriveIssuers(board.entries);
  return league(totals.map((i) => ({ key: i.id, label: i.name, valueUsd: i.value_usd, entries: i.entries })));
}

export function chainLeague(board: Pick<ClassBoard, "chains" | "entries">): LeagueRow[] {
  const totals = board.chains.length ? board.chains : deriveChains(board.entries);
  return league(totals.map((c) => ({ key: c.chain, label: chainLabel(c.chain), valueUsd: c.value_usd, entries: c.entries })));
}

/** How many entries are valued each way, for the value-basis tile. */
export function entriesByBasis(entries: readonly ClassEntry[]): Record<ValueBasis, number> {
  const counts: Record<ValueBasis, number> = { market_cap: 0, tvl: 0 };
  for (const entry of entries) counts[entry.value_basis] += 1;
  return counts;
}

/** Board totals from the entries (the collector's rule), for fixtures and as a check on a board. */
export function totalsFromEntries(all: readonly ClassEntry[]): ClassBoard["totals"] {
  const entries = distributedEntries(all);
  const represented = all.filter(isRepresented);
  const value = sumOrNull(entries.map((e) => e.value_usd));
  const representedValue = represented.some((e) => e.value_usd === null) ? null : represented.reduce((n, e) => n + (e.value_usd as number), 0);
  return {
    ...(represented.length
      ? {
          represented_entries: represented.length,
          represented_value_usd: representedValue,
          total_incl_represented_usd: value === null || representedValue === null ? null : value + representedValue,
        }
      : {}),
    entries: entries.length,
    priced: entries.filter((e) => e.price !== null).length,
    supplied: entries.filter((e) => e.supply !== null).length,
    valued: entries.filter((e) => e.value_usd !== null).length,
    value_usd: value,
    by_basis: {
      market_cap: sumOrNull(entries.filter((e) => e.value_basis === "market_cap").map((e) => e.value_usd)),
      tvl: sumOrNull(entries.filter((e) => e.value_basis === "tvl").map((e) => e.value_usd)),
    },
  };
}

// ---------------------------------------------------------------- headline overlap (U-20)

/** Short class names for the overlap sentence: "Treasuries", "Govt debt". */
const classShort = (apiId: string): string => ASSET_CLASSES.find((c) => c.apiId === apiId)?.short ?? apiId;

const overlapReason = (o: Pick<ClassOverlap, "reason">): OverlapReason => (o.reason === "backed_by" ? "backed_by" : "same_token");

/** A reserve position that is another stablecoin rather than a fund. */
export const holdsStablecoin = (o: Pick<ClassOverlap, "class" | "backing">): boolean => o.backing?.target === "stablecoin" || o.class === "stablecoin";

/**
 * The note under the all-class total, from the live overlap: null when nothing
 * was deducted. "$6.82B in funds listed as both Treasuries and stablecoins",
 * "…or held in stablecoin reserves" once reserves are deducted too.
 */
export function overlapNote(
  index: Pick<ClassIndex, "overlap" | "overlap_value_usd" | "overlap_unchecked" | "venue_overlap">,
  money: (value: number) => string,
): string | null {
  const parts: string[] = [];
  const once = deductedNote(index, money);
  if (once) parts.push(once);
  // A class-valued contract that is also a stock venue token: counted twice, and said so.
  const venue = index.venue_overlap ?? [];
  if (venue.length) {
    const classes = [...new Set(venue.map((v) => ASSET_CLASSES.find((c) => c.apiId === v.class)?.label ?? v.class))].join(", ");
    const known = venue.filter((v) => v.value_usd !== null);
    const sum = known.reduce((total, v) => total + v.value_usd!, 0);
    parts.push(
      `${venue.length} ${venue.length === 1 ? "product is" : "products are"} counted both in ${classes} and under Stocks${known.length ? ` (${money(sum)})` : ""}; not deducted yet.`,
    );
  }
  if (!parts.length) return null;
  const unchecked = Math.max(0, (index.overlap_unchecked?.length ?? 0) - venue.length);
  return parts.join(" ") + (unchecked ? ` ${unchecked} more ${unchecked === 1 ? "match" : "matches"} could not be checked.` : "");
}

/** "Counts each product once — $8.54B in … is counted once." Null when nothing was deducted. */
function deductedNote(index: Pick<ClassIndex, "overlap" | "overlap_value_usd">, money: (value: number) => string): string | null {
  const deducted = (index.overlap ?? []).filter((o) => o.deducted);
  const value = index.overlap_value_usd ?? null;
  if (!deducted.length || value === null || value <= 0) return null;
  const listedTwice = deducted.filter((o) => overlapReason(o) === "same_token");
  const classes = [...new Set(listedTwice.map((o) => classShort(o.class)))];
  const listed = classes.length === 1 ? classes[0]! : `${classes.slice(0, -1).join(", ")} and ${classes.at(-1)!}`;
  const held = deducted.filter((o) => overlapReason(o) === "backed_by");
  const heldFunds = held.some((o) => !holdsStablecoin(o));
  const heldCoins = held.some(holdsStablecoin);
  const heldWhat = heldFunds && heldCoins ? "funds and stablecoins" : heldCoins ? "stablecoins" : "funds";
  const what = !listedTwice.length
    ? `${heldWhat} held in stablecoin reserves`
    : `funds listed as both ${listed} and stablecoins${held.length ? `, or ${heldCoins ? `${heldWhat} ` : ""}held in stablecoin reserves,` : ""}`;
  return `Counts each product once — ${money(value)} in ${what} is counted once.`;
}

export interface OverlapGroup {
  reason: OverlapReason;
  /** "Listed as a stablecoin too", "Held in a stablecoin's reserve". */
  label: string;
  items: ClassOverlap[];
  /** Summed `value_usd`; null when none is known. */
  value: number | null;
}

const OVERLAP_GROUP_LABELS: Record<OverlapReason, string> = {
  same_token: "Listed as a stablecoin too",
  backed_by: "Held in a stablecoin's reserve",
};

/** The deducted overlap grouped by reason, same token first, each group largest first. */
export function overlapGroups(overlap: readonly ClassOverlap[] | undefined): OverlapGroup[] {
  const deducted = (overlap ?? []).filter((o) => o.deducted);
  return (["same_token", "backed_by"] as const)
    .map((reason) => {
      const items = deducted.filter((o) => overlapReason(o) === reason).sort((a, b) => (b.value_usd ?? -1) - (a.value_usd ?? -1));
      const known = items.filter((o) => o.value_usd !== null);
      return { reason, label: OVERLAP_GROUP_LABELS[reason], items, value: known.length ? known.reduce((sum, o) => sum + o.value_usd!, 0) : null };
    })
    .filter((group) => group.items.length > 0);
}
